import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RcaThunkArg } from "../../actions/actionTypes";
import { IRcaTest } from "../../models/IRcaTest";
import { IRcaTestCategory } from "../../models/IRcaTestCategory";
import { ITestSelectionState } from "../../store/IRcaState";
import { Utils } from "../../Utils";

/**
 * Filters a set of tests using a search query.
 *
 * @param tests - The set of tests.
 * @param searchQuery - The search query to use for filtering.
 * @returns - The filtered set of tests.
 */
const filterTests = (tests: IRcaTest[], searchQuery: string): IRcaTest[] => {
  if (!searchQuery) {
    return tests;
  }

  const search = new RegExp(Utils.escapeRegExp(searchQuery), "i");
  return tests.filter((t) => search.test(t.name) || (t.keywords ? search.test(t.keywords) : false));
};

/**
 * The initial state for the test selection slice.
 */
export const initialState: ITestSelectionState = {
  testCategories: [],
  availableTestsSearchQuery: "",
  availableTests: [],
  availableTestsFiltered: [],
};

/**
 * Loads the available test categories.
 */
export const loadTestCategories = createAsyncThunk<IRcaTestCategory[], string, RcaThunkArg>(
  "testSelection/loadCategories",
  async (language, thunkApi) => thunkApi.extra.testCatalog.getTestCategoriesAsync(language)
);

/**
 * Loads the available tests for a category.
 */
interface ILoadTestsRequest {
  /**
   * The current language.
   */
  language: string;

  /**
   * The selected category.
   */
  category: string;
}

/**
 * Loads the available tests for a category.
 */
export const loadTests = createAsyncThunk<IRcaTest[], ILoadTestsRequest, RcaThunkArg>(
  "testSelection/loadTests",
  async ({ language, category }, thunkApi) => thunkApi.extra.testCatalog.getTestsAsync(language, category)
);

const testSelectionSlice = createSlice({
  name: "testSelection",
  initialState,
  reducers: {
    searchTests: (state, action: PayloadAction<string>) => {
      state.availableTestsSearchQuery = action.payload;
      state.availableTestsFiltered = filterTests(state.availableTests, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadTestCategories.fulfilled, (state, action) => {
      state.testCategories = action.payload;
    });
    builder.addCase(loadTests.fulfilled, (state, action) => {
      state.availableTests = action.payload;
      state.availableTestsFiltered = action.payload;
      state.availableTestsSearchQuery = "";
    });
  },
});

export const { searchTests } = testSelectionSlice.actions;

export default testSelectionSlice.reducer;
